import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Paper, Card, CardContent, Button,
  Chip, CircularProgress, Alert, Divider, Tabs, Tab,
  IconButton, Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Translate as TranslateIcon,
  VolumeUp as AudioIcon,
  Publish as PublishIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';
import StoryPathNav from '../../components/admin/StoryPathNav';

const StoryDetail = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();

  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedTranslation, setSelectedTranslation] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchStoryData();
  }, [storyId]);

  const fetchStoryData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.admin.getStory(storyId);
      setStory(response.data.story);

      // Select first translation to display
      if (response.data.story.translations && response.data.story.translations.length > 0) {
        setSelectedTranslation(response.data.story.translations[0]);
      }
    } catch (error) {
      console.error('Error fetching story:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStory = async () => {
    if (!window.confirm('Are you sure you want to delete this story? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      await apiClient.admin.deleteStory(storyId);
      navigate(`/admin/paths/${story.path_id}`);
    } catch (error) {
      console.error('Error deleting story:', error);
      alert('Failed to delete story');
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getLanguageName = (code) => {
    const languages = {
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'ja': 'Japanese',
      'ko': 'Korean',
      'zh': 'Chinese'
    };
    return languages[code] || code.toUpperCase();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!story) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>Story not found</Alert>
      </Box>
    );
  }

  const translations = story.translations || [];
  const hasTranslations = translations.length > 0;
  const allLanguages = ['en', 'es', 'fr', 'de', 'it', 'pt', 'ja', 'ko', 'zh'];
  const missingLanguages = allLanguages.filter(
    lang => !translations.some(t => t.language_code === lang)
  );

  return (
    <Box sx={{ pb: 10 }}>
      {/* Jump between stories in this path */}
      <StoryPathNav buildTo={(id) => `/admin/stories/${id}`} />

      {/* Header - Mobile Optimized */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ mb: 3, gap: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
          <IconButton
            onClick={() => navigate(story.path ? `/admin/paths/${story.path.path_id}` : '/admin/stories')}
            aria-label="Back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
            >
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {story.title}
              </Typography>
              {story.is_published ? (
                <Chip icon={<PublishIcon />} label="Published" color="success" size="small" variant="outlined" />
              ) : (
                <Chip label="Draft" variant="outlined" size="small" />
              )}
            </Stack>
            {story.path && (
              <Typography variant="body2" color="text.secondary">
                Path: {story.path.title} ({story.path.level})
              </Typography>
            )}
          </Box>
        </Box>

        {/* Action Buttons - Stack on Mobile */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<TranslateIcon />}
            onClick={() => navigate(`/admin/stories/${storyId}/translations`)}
            fullWidth={{ xs: true, sm: false }}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Manage Translations
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/stories/${storyId}/edit`)}
            fullWidth={{ xs: true, sm: false }}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            onClick={handleDeleteStory}
            disabled={deleting}
            fullWidth={{ xs: true, sm: false }}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Delete
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        {/* Story Info - Full Width on Mobile */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={2}>
            {/* Story Information Card */}
            <Grid item xs={12} sm={6} lg={12}>
              <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
                    Story Information
                  </Typography>
                  <Divider sx={{ mb: 2, borderColor: 'divider' }} />

                  <Box sx={{ display: 'grid', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Order in Path
                      </Typography>
                      <Typography variant="body1">{story.order_index}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Difficulty Rating
                      </Typography>
                      <Typography variant="body1">{story.difficulty_rating}/5</Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Status
                      </Typography>
                      <Typography variant="body1">
                        {story.status || 'draft'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Created
                      </Typography>
                      <Typography variant="body1">{formatDate(story.created_at)}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Updated
                      </Typography>
                      <Typography variant="body1">{formatDate(story.updated_at)}</Typography>
                    </Box>

                    {story.tags && story.tags.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Tags
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {story.tags.map(tag => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Translation Coverage Card */}
            <Grid item xs={12} sm={6} lg={12}>
              <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
                    Translation Coverage
                  </Typography>
                  <Divider sx={{ mb: 2, borderColor: 'divider' }} />

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Available in {translations.length} of {allLanguages.length} languages
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                      {allLanguages.map(lang => {
                        const hasTranslation = translations.some(t => t.language_code === lang);
                        return (
                          <Chip
                            key={lang}
                            label={lang.toUpperCase()}
                            size="small"
                            variant="outlined"
                            color={hasTranslation ? 'success' : 'default'}
                            icon={hasTranslation ? <CheckIcon /> : <CloseIcon />}
                          />
                        );
                      })}
                    </Box>
                  </Box>

                  {missingLanguages.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                      Missing: {missingLanguages.map(l => getLanguageName(l)).join(', ')}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Content View - Mobile Optimized */}
        <Grid item xs={12} lg={8}>
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
              Story Content & Translations
            </Typography>

            {!hasTranslations ? (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                No translations available yet.
                <Button
                  size="small"
                  sx={{ ml: 2, textTransform: 'none' }}
                  onClick={() => navigate(`/admin/stories/${storyId}/translations`)}
                >
                  Add Translations
                </Button>
              </Alert>
            ) : (
              <>
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => {
                    setActiveTab(newValue);
                    setSelectedTranslation(translations[newValue]);
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
                >
                  {translations.map((trans) => (
                    <Tab
                      key={trans.language_code}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getLanguageName(trans.language_code)}
                          {trans.has_audio && <AudioIcon fontSize="small" color="success" />}
                        </Box>
                      }
                    />
                  ))}
                </Tabs>

                {selectedTranslation && (
                  <Box>
                    {/* Translation Info */}
                    <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Chip
                        label={selectedTranslation.is_auto_translated ? 'Auto-translated' : 'Manual'}
                        size="small"
                        color={selectedTranslation.is_auto_translated ? 'default' : 'primary'}
                        variant="outlined"
                      />
                      {selectedTranslation.has_audio ? (
                        <Chip
                          icon={<AudioIcon />}
                          label="Audio available"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label="No audio"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {/* Translated Title */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Title
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {selectedTranslation.translated_title}
                      </Typography>
                    </Box>

                    {/* Content Grid - Stack on Mobile */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Original Content
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            maxHeight: { xs: 300, md: 400 },
                            overflow: 'auto',
                            bgcolor: 'action.hover'
                          }}
                        >
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {selectedTranslation.content || 'No original content'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Translation
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            maxHeight: { xs: 300, md: 400 },
                            overflow: 'auto',
                            bgcolor: 'action.hover'
                          }}
                        >
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {selectedTranslation.translation || 'No translation'}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Metadata */}
                    <Box sx={{ mt: 3, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        Created: {formatDate(selectedTranslation.created_at)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Updated: {formatDate(selectedTranslation.updated_at)}
                      </Typography>
                      {selectedTranslation.audio_job_id && (
                        <Typography variant="caption" color="text.secondary">
                          Audio ID: {selectedTranslation.audio_job_id}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Paper>

          {/* Glossary Section */}
          {story.glossary && story.glossary.length > 0 && (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mt: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
                Glossary Items
              </Typography>
              <Grid container spacing={2}>
                {story.glossary.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          {item.language_code.toUpperCase()}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {item.word}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.translation}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default StoryDetail;
